/**
 * Local Storage Keys
 */
export const STORAGE_KEYS = {
  USER_PROFILE: 'kma_user_profile',
  PRACTICE_SESSIONS: 'kma_practice_sessions',
  PREFERENCES: 'kma_preferences',
  CONCEPT_MASTERY: 'kma_concept_mastery',
  CONCEPT_PROGRESS: 'kma_concept_progress',
  GUEST_QUESTION_COUNT: 'kma_guest_count',
  LAST_SYNC: 'kma_last_sync',
  BIOMETRIC_CREDENTIALS: 'kma_biometric_creds',
  INCOMPLETE_SESSION: 'kma_incomplete_session', // For session recovery
  LAST_ACTIVITY: 'kma_last_activity', // For timeout tracking
} as const;

/**
 * Migrate old localStorage keys to new terminology
 */
function migrateOldStorageKeys(): void {
  try {
    // Migrate old game_sessions key to practice_sessions (v7.7.x migration)
    const oldSessions = localStorage.getItem('kma_game_sessions');
    if (oldSessions) {
      localStorage.setItem('kma_practice_sessions', oldSessions);
      localStorage.removeItem('kma_game_sessions');
    }
  } catch {
    // Silent fail - localStorage may be unavailable (private browsing, quota exceeded)
  }
}

// Run migration on module load
migrateOldStorageKeys();

/**
 * Generic localStorage helper with TypeScript support
 */
export const storage = {
  get: <T>(key: string): T | null => {
    try {
      const item = localStorage.getItem(key);
      if (!item) return null;

      const parsed = JSON.parse(item) as unknown;
      // Note: We allow null/undefined as legitimate stored values
      // The return type T | null already handles this case
      return parsed as T;
    } catch (error: unknown) {
      // Invalid JSON or localStorage access error
      console.warn(`Failed to parse storage key "${key}":`, error);
      return null;
    }
  },

  set: <T>(key: string, value: T): void => {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch {
      // Silent fail - localStorage may be unavailable
    }
  },

  remove: (key: string): void => {
    try {
      localStorage.removeItem(key);
    } catch {
      // Silent fail - localStorage may be unavailable
    }
  },

  clear: (): void => {
    try {
      // Only clear app-specific keys
      Object.values(STORAGE_KEYS).forEach((key) => {
        localStorage.removeItem(key);
      });
    } catch {
      // Silent fail - localStorage may be unavailable
    }
  },
};
